/* eslint-disable max-lines-per-function */
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { getExportFileName, getExportFileNameWithDate } from '../Delivery/utils';
import { useAuth } from '../../Hooks/useAuth';
import { serviceName } from '../../Api/resources/serviceConstants';
import { EMPLOYEE_MASTER } from '../../Api/resources/endPoint';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../Api/resources/serviceURLs';

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.LANDING_MS);
  const servicePort = appendServicePortToURL(serviceName.LANDING_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${EMPLOYEE_MASTER}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${EMPLOYEE_MASTER}`;
};

const baseURL = fetchBaseURL();
const PAGE_SIZE = 10;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  return date
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .replace(/ /g, '-');
};

const ChangeHistory = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const tableStateRef = useRef(null);

  const empNo = location.state?.empNo || '';
  const empName = location.state?.empName || '';
  const isInitialFilterApplied = useRef(false);

  // EXACT PATTERN FROM EmployeeManagement.js / changeRequest.js
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });

  const handlePaginationChange = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return response; // CRITICAL: Return response

    try {
      const payload = response.data?.data || response.data || {};
      let tableConfiguration = payload.tableConfiguration || [];
      if (empName && !isInitialFilterApplied.current && tableConfiguration.length > 0) {
        tableConfiguration = tableConfiguration.map((column) => {
          if (column.field === 'employee_name') {
            return {
              ...column,
              menuItems: (column.menuItems || []).map((menu) => ({
                ...menu,
                items: (menu.items || []).map((item) => {
                  if (item.type === 'filter') {
                    return {
                      ...item,
                      selectedValue: { value: empName, label: empName },
                      selectedValues: [{ value: empName, label: empName }],
                    };
                  }
                  return item;
                }),
              })),
            };
          }
          return column;
        });

        if (response.data?.data) {
          response.data.data.tableConfiguration = tableConfiguration;
        } else if (response.data) {
          response.data.tableConfiguration = tableConfiguration;
        }
      }
      const paginationData = response.meta || response.data?.meta || {};
      const safePage = Number(paginationData.page) || 1;
      const safeLimit = Number(paginationData.pageSize || paginationData.limit) || PAGE_SIZE;
      const safeTotal = Number(paginationData.total) || 0;

      // 1. Update the React state (so UI does not throw NaN)
      setPagination((prev) => ({
        ...prev,
        page: safePage,
        limit: safeLimit,
        total: safeTotal,
        totalPages: paginationData.totalPages,
      }));
      response.meta = {
        ...paginationData,
        page: safePage,
        limit: safeLimit,
        pageSize: safeLimit,
        total: safeTotal,
        totalRecords: safeTotal,
      };

      if (response.data) {
        response.data.pagination = response.meta;
        response.data.meta = response.meta;
      }
      const rawData = Array.isArray(payload.tableData) ? payload.tableData : [];
      const formattedData = rawData.map((row, index) => ({
        ...row,
        slNo: (safePage - 1) * safeLimit + index + 1, // NaN is impossible now
        effective_from: formatDate(row.effective_from),
        effective_to: formatDate(row.effective_to),
        created_at: formatDate(row.created_at),
      }));
      if (response.data?.data) {
        response.data.data.tableData = formattedData;
      } else if (response.data) {
        response.data.tableData = formattedData;
      }

    } catch (error) {
      console.error('Pagination formatting error:', error);
    }

    return response;
  }, [empName]);

  useEffect(() => {
    if (!empName || isInitialFilterApplied.current) return;

    const timer = setTimeout(() => {
      const tableApi = tableStateRef.current?.();
      if (!tableApi) return;

      const existingFilters = tableApi.filters || [];
      const hasEmpFilter = existingFilters.some((item) => item.field === 'employee_name');
      if (!hasEmpFilter) {
        const updatedFilters = [
          ...existingFilters,
          {
            field: 'employee_name',
            selectedValue: { value: empName, label: empName },
            selectedValues: [{ value: empName, label: empName }],
          },
        ];

        tableApi.setFilters?.(updatedFilters);
        tableApi.fetchTableData?.(
          updatedFilters,
          { limit: PAGE_SIZE, page: 1 },
          '',
        );
      }

      isInitialFilterApplied.current = true;
    }, 500);

    return () => clearTimeout(timer);
  }, [empName]);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL: `${baseURL}/history`,
        showPagination: true,
        showToolBar: true,
        userInfo: {
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || {},
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        headers: { token: userProfile?.access_token || '' },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('Employee_Change_History'),
            sheetName: () => getExportFileNameWithDate('Change_History'),
          },
        },
        additionalPayloadData: { empNo },
        handler: {
          handleFilteredApiResponse,
          setTableStateHandler: (getState) => {
            tableStateRef.current = getState;
          },
        },
        toolBarSettings: {
          showToolBarHeading: true,
          toolBarHeading: 'Employee Management History',
          showFilterOption: true,
          showReset: false,
          showDownloadExcel: false,
          showDownloadCsv: true,
        },
      },
      showFilterOption: true,
      tableSettings: {
        tableConfig: [],
        size: 'large',
        loaderSkeleton: { rows: 5, bodyCells: 1, columns: 8 },
        enableTreeTable: false,
        pageSize: PAGE_SIZE,
        paginationConfig: {
          ...pagination,
          onPaginationChange: handlePaginationChange,
          rowsPerPageOptions: [10, 15, 25, 50],
        },
        showExpandCollapse: false,
        tableScrollHeight: '650px',
      },
    }),
    [empNo, empName, userProfile, handleFilteredApiResponse, handlePaginationChange],
  );

  return (
    <div className="emp-mgmt-tu">
      <TableUtility configuration={configuration} />
    </div>
  );
};

export default ChangeHistory;
